import React from 'react';
import { useSelector } from 'react-redux';
import type {
  AdaptiveCustomerText,
  PerTeamTextGroup,
  PerUserTypeTextGroup,
  SubscriptionInfo,
  TextContent,
} from 'redux/modules/subscriptions.types';
import { getSubscriptionInfo } from 'redux/selectors/subscriptions';
import { isFunction } from 'utils/safeInvoke';

const getPerTeamText = (group: PerTeamTextGroup, isTeam: boolean): TextContent | undefined => {
  if (isTeam && group.team) {
    return group.team;
  }
  if (!isTeam && group.individual) {
    return group.individual;
  }
  return group.default;
};

const getAdaptiveCustomerText = (
  text: AdaptiveCustomerText,
  subscriptionInfo: SubscriptionInfo
): TextContent => {
  const { isAdmin, isEnterprise, isTeam } = subscriptionInfo;

  if (typeof text === 'string' || React.isValidElement(text)) {
    return text;
  }

  const safeText = text as PerUserTypeTextGroup;

  if (isAdmin && safeText.admin) {
    if (isEnterprise && safeText.admin.enterprise !== undefined) {
      return getPerTeamText(safeText.admin.enterprise, isTeam) || safeText.default;
    } else if (!isEnterprise && safeText.admin.nonEnterprise !== undefined) {
      return getPerTeamText(safeText.admin.nonEnterprise, isTeam) || safeText.default;
    } else {
      return safeText.admin.default || safeText.default;
    }
  } else if (!isAdmin && safeText.normal !== undefined) {
    return safeText.normal;
  } else if (!isAdmin && safeText.limited !== undefined) {
    return getPerTeamText(safeText.limited, isTeam) || safeText.default;
  }

  return safeText.default;
};

interface Props {
  text?: AdaptiveCustomerText;
  subscriptionInfo: SubscriptionInfo;
}

export const AdaptiveText: React.FC<Props> = props => {
  if (!props.text) {
    return null;
  }

  const adaptiveCustomerText = getAdaptiveCustomerText(props.text, props.subscriptionInfo);

  if (isFunction(adaptiveCustomerText)) {
    return <>{adaptiveCustomerText(props.subscriptionInfo)}</>;
  }

  return <>{adaptiveCustomerText}</>;
};

export const AdaptiveTextConnected: React.FC<Omit<Props, 'subscriptionInfo'>> = props => {
  const subscriptionInfo = useSelector(getSubscriptionInfo);
  return <AdaptiveText text={props.text} subscriptionInfo={subscriptionInfo} />;
};

export default AdaptiveTextConnected;
